import axios, {AxiosError} from 'axios';
import {API_URL, MASTER_NAME} from './constants';

let isRefreshing: boolean = false;
let failedQueue: any[] = [];

const processQueue = (error: AxiosError | null, token = null) => {
  failedQueue.forEach(prom => {
    error ? prom.reject(error) : prom.resolve(token);
  });
  failedQueue = [];
};

const createApiClient = (customHeaders = {}) => {
  const instance = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      Master: MASTER_NAME,
      ...customHeaders,
    },
  });

  instance.interceptors.request.use(
    config => {
      const token = localStorage.getItem('token');
      if (token) {
        const {access_token, refresh_token} = JSON.parse(token);
        config.headers['X-Authorization'] = `Bearer ${access_token}`;
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    },
  );

  instance.interceptors.response.use(
    res => {
      return res;
    },
    async error => {
      const token = localStorage.getItem('token');
      if (token) {
        const {refresh_token} = JSON.parse(token);
        const statusCode = error?.response?.data?.error.code;
        const statusMsg = error?.response?.data?.error.message;
        const originalRequest = error?.config;
        if (error?.response?.status === 401 && !originalRequest._retry) {
          if (isRefreshing) {
            return new Promise(function refFunc(resolve, reject) {
              failedQueue.push({resolve, reject});
            })
              .then(token => {
                originalRequest.headers['X-Authorization'] = `Bearer ` + token;
                return instance(originalRequest);
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }
          originalRequest._retry = true;
          isRefreshing = true;

          const formData = new FormData();
          formData.append('refresh_token', refresh_token);

          const branch = localStorage.getItem('branch');
          return axios
            .post(
              API_URL + `refresh?branch=${branch}`,
              {
                refresh_token: refresh_token,
              },
              {
                headers: {'Content-Type': 'multipart/form-data'},
              },
            )
            .then(response => {
              if (response?.data) {
                const returnData = response.data;
                localStorage.setItem(
                  'token',
                  JSON.stringify({
                    access_token: returnData.access_token,
                    refresh_token: returnData.refresh_token,
                  }),
                );

                instance.defaults.headers.common['X-Authorization'] =
                  `Bearer ` + returnData?.access_token;
                processQueue(null, returnData?.access_token);

                return instance(originalRequest);
              }
              console.log(response);
              processQueue(error, null);
              localStorage.removeItem('token');
              window.location.href = '/login';
            })
            .catch(error => {
              processQueue(error, null);
              localStorage.removeItem('token');
              window.location.href = '/login';
            })
            .finally(() => {
              isRefreshing = false;
            });
        }
      }
    },
  );

  return instance;
};

export const api = (customHeaders = {}) => {
  return {
    get: <T>(url: string, params?: object) =>
      createApiClient(customHeaders).get<T>(url, params),
    post: <T>(url: string, data: any) =>
      createApiClient(customHeaders).post<T>(url, data),
    patch: <T>(url: string, data: any) =>
      createApiClient(customHeaders).patch<T>(url, data),
    put: <T>(url: string, data: any) =>
      createApiClient(customHeaders).put<T>(url, data),
    delete: <T>(url: string, data?: any) =>
      createApiClient(customHeaders).delete<T>(url, {data}),
  };
};
